import {
    ActionIcon,
    Button,
    Center,
    Divider,
    Group,
    Loader,
    Paper,
    SegmentedControl,
    Select,
    Stack,
    Text,
    TextInput,
    Title,
} from '@mantine/core'
import {type DatesRangeValue} from '@mantine/dates'
import {IconSearch, IconX} from '@tabler/icons-react'
import {useMemo} from 'react'

import {useCustomers} from '../customers/use-customers'
import {JobsDateFilter} from '../jobs/jobs-date-filter'
import {type MineOrAll, useMineOrAll} from '../lib/use-mine-or-all'
import {FilterMultiSelectButton, FilterToggleButton} from '../ui/list-filter-controls'
import {ProjectListCard} from './project-list-card'
import {type ProjectSortField, type ProjectSortState} from './projects-search'
import type {ProjectLifecycleStatus, ProjectListFilters, ProjectListItem} from './schemas'
import {useProjects} from './use-projects'
import {useProjectsListFilters} from './use-projects-list-filters'

const SERVER_SORTED_FIELDS = new Set<ProjectSortField>(['createdAt', 'earliestStart', 'jobCount'])

const SORT_OPTIONS: Array<{value: string; label: string; state: ProjectSortState}> = [
    {
        value: 'createdAt-desc',
        label: 'Created (newest first)',
        state: {field: 'createdAt', direction: 'desc'},
    },
    {
        value: 'createdAt-asc',
        label: 'Created (oldest first)',
        state: {field: 'createdAt', direction: 'asc'},
    },
    {
        value: 'earliestStart-asc',
        label: 'Earliest start (earliest first)',
        state: {field: 'earliestStart', direction: 'asc'},
    },
    {
        value: 'earliestStart-desc',
        label: 'Earliest start (latest first)',
        state: {field: 'earliestStart', direction: 'desc'},
    },
    {
        value: 'jobCount-desc',
        label: 'Job count (most first)',
        state: {field: 'jobCount', direction: 'desc'},
    },
    {
        value: 'jobCount-asc',
        label: 'Job count (fewest first)',
        state: {field: 'jobCount', direction: 'asc'},
    },
    {value: 'name-asc', label: 'Name (A-Z)', state: {field: 'name', direction: 'asc'}},
    {value: 'name-desc', label: 'Name (Z-A)', state: {field: 'name', direction: 'desc'}},
    {
        value: 'customerName-asc',
        label: 'Customer (A-Z)',
        state: {field: 'customerName', direction: 'asc'},
    },
    {
        value: 'customerName-desc',
        label: 'Customer (Z-A)',
        state: {field: 'customerName', direction: 'desc'},
    },
    {value: 'status-asc', label: 'Status (A-Z)', state: {field: 'status', direction: 'asc'}},
    {value: 'status-desc', label: 'Status (Z-A)', state: {field: 'status', direction: 'desc'}},
]

const STATUS_FILTER_OPTIONS: Array<{value: ProjectLifecycleStatus; label: string}> = [
    {value: 'estimate', label: 'Estimate'},
    {value: 'in_progress', label: 'Active'},
    {value: 'closed', label: 'Closed'},
]

function dateRangeIsActive(range: DatesRangeValue): boolean {
    return range[0] != null || range[1] != null
}

function hasActiveFilters(
    name: string,
    customerId: string | null,
    scheduleDateRange: DatesRangeValue,
    statusFilter: ProjectLifecycleStatus[],
    hasUnbilled: boolean,
) {
    return Boolean(
        name.trim() ||
        customerId ||
        dateRangeIsActive(scheduleDateRange) ||
        statusFilter.length > 0 ||
        hasUnbilled,
    )
}

function compareProjectStatus(left: ProjectLifecycleStatus, right: ProjectLifecycleStatus) {
    const order: Record<ProjectLifecycleStatus, number> = {
        estimate: 0,
        in_progress: 1,
        closed: 2,
    }
    return order[left] - order[right]
}

function compareProjectListItems(
    left: ProjectListItem,
    right: ProjectListItem,
    field: ProjectSortField,
) {
    if (field === 'status') {
        return compareProjectStatus(left.status, right.status)
    }

    const leftValue = String(left[field])
    const rightValue = String(right[field])
    return leftValue.localeCompare(rightValue)
}

function sortProjectListItems(projects: ProjectListItem[], sortState: ProjectSortState) {
    const directionMultiplier = sortState.direction === 'asc' ? 1 : -1
    return [...projects].sort(
        (left, right) =>
            directionMultiplier * compareProjectListItems(left, right, sortState.field),
    )
}

function listSortBy(field: ProjectSortField): ProjectListFilters['sortBy'] {
    if (field === 'createdAt') {
        return 'created_at'
    }
    if (field === 'earliestStart') {
        return 'earliest_start'
    }
    if (field === 'jobCount') {
        return 'job_count'
    }
    return undefined
}

function listSortDir(direction: ProjectSortState['direction']): ProjectListFilters['sortDir'] {
    return direction
}

type ProjectsPageProps = {
    embedded?: boolean
    syncUrlSearch?: boolean
    showMineOrAll?: boolean
}

function sortOptionValue(sortState: ProjectSortState) {
    return `${sortState.field}-${sortState.direction}`
}

function filterClearButton(value: string, onClear: () => void) {
    if (!value) {
        return null
    }

    return (
        <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={onClear}>
            <IconX size={14} />
        </ActionIcon>
    )
}

export function ProjectsPage({
    embedded = false,
    syncUrlSearch = true,
    showMineOrAll = !embedded,
}: ProjectsPageProps) {
    const {
        nameFilter,
        setNameFilter,
        customerIdFilter,
        setCustomerIdFilter,
        scheduleDateRange,
        setScheduleDateRange,
        statusFilter,
        setStatusFilter,
        sortState,
        setSortState,
        hasUnbilledFilter,
        setHasUnbilledFilter,
        debouncedName,
    } = useProjectsListFilters(syncUrlSearch)
    const [scope, setScope] = useMineOrAll('projects')

    const scheduleDateFromFilter = scheduleDateRange[0]
    const scheduleDateToFilter = scheduleDateRange[1]

    const sortBy = listSortBy(sortState.field)
    const sortDir = sortBy ? listSortDir(sortState.direction) : undefined

    const listFilters: ProjectListFilters = {
        name: debouncedName || undefined,
        customerId: customerIdFilter ?? undefined,
        startDateFrom: scheduleDateFromFilter ?? undefined,
        startDateTo: scheduleDateToFilter ?? undefined,
        status: statusFilter.length > 0 ? statusFilter : undefined,
        sortBy,
        sortDir,
        hasUnbilled: hasUnbilledFilter || undefined,
        mine: showMineOrAll && scope === 'mine' ? true : undefined,
    }

    const {data: projects = [], isLoading} = useProjects(listFilters)

    const filtersActive = hasActiveFilters(
        nameFilter,
        customerIdFilter,
        scheduleDateRange,
        statusFilter,
        hasUnbilledFilter,
    )

    const sortedProjects = useMemo(() => {
        if (SERVER_SORTED_FIELDS.has(sortState.field)) {
            return projects
        }
        return sortProjectListItems(projects, sortState)
    }, [projects, sortState])

    const {data: customers = [], isLoading: isLoadingCustomers} = useCustomers()
    const customerOptions = useMemo(
        () => customers.map((customer) => ({value: customer.id, label: customer.name})),
        [customers],
    )

    return (
        <>
            {!embedded ? (
                <Group justify="space-between" mb="md">
                    <Title order={1}>Orders</Title>
                </Group>
            ) : null}

            <Stack gap="md">
                <Paper withBorder p="sm" radius="md">
                    <Group gap="xs" wrap="wrap" align="center">
                        {showMineOrAll ? (
                            <SegmentedControl
                                aria-label="Whose orders"
                                size="sm"
                                value={scope}
                                onChange={(value) => setScope(value as MineOrAll)}
                                data={[
                                    {value: 'mine', label: 'Mine'},
                                    {value: 'all', label: 'All'},
                                ]}
                            />
                        ) : null}
                        <TextInput
                            aria-label="Name"
                            placeholder="Search orders or numbers..."
                            leftSection={<IconSearch size={16} stroke={1.5} />}
                            rightSection={filterClearButton(nameFilter, () => setNameFilter(''))}
                            value={nameFilter}
                            onChange={(event) => setNameFilter(event.currentTarget.value)}
                            style={{flex: '1 1 150px', minWidth: 140}}
                        />

                        <FilterMultiSelectButton
                            label="Status"
                            description="Filter by order status"
                            options={STATUS_FILTER_OPTIONS}
                            value={statusFilter}
                            primaryValue="in_progress"
                            onChange={setStatusFilter}
                        />

                        <Select
                            aria-label="Customer"
                            placeholder={isLoadingCustomers ? 'Loading customers...' : 'Customer'}
                            clearable
                            searchable
                            disabled={isLoadingCustomers}
                            data={customerOptions}
                            value={customerIdFilter}
                            onChange={setCustomerIdFilter}
                            nothingFoundMessage="No customers found"
                            w={170}
                        />

                        <JobsDateFilter value={scheduleDateRange} onChange={setScheduleDateRange} />

                        <FilterToggleButton
                            label="Unbilled"
                            accessibleLabel="Has unbilled charges"
                            checked={hasUnbilledFilter}
                            onChange={setHasUnbilledFilter}
                        />

                        <Divider orientation="vertical" visibleFrom="sm" />

                        <Select
                            aria-label="Sort by"
                            data={SORT_OPTIONS}
                            value={sortOptionValue(sortState)}
                            onChange={(value) => {
                                const option = SORT_OPTIONS.find((opt) => opt.value === value)
                                if (option) {
                                    setSortState(option.state)
                                }
                            }}
                            w={190}
                            allowDeselect={false}
                        />
                    </Group>
                </Paper>

                <Group justify="space-between" align="center">
                    <Group gap="xs" align="center">
                        <Text size="sm" c="dimmed">
                            {sortedProjects.length === 1
                                ? '1 order'
                                : `${sortedProjects.length} orders`}
                        </Text>
                        {filtersActive && (
                            <Button
                                variant="subtle"
                                size="compact-sm"
                                onClick={() => {
                                    setNameFilter('')
                                    setCustomerIdFilter(null)
                                    setScheduleDateRange([null, null])
                                    setStatusFilter([])
                                    setHasUnbilledFilter(false)
                                }}
                            >
                                Clear all filters
                            </Button>
                        )}
                    </Group>
                </Group>

                {isLoading ? (
                    <Center py="xl">
                        <Loader />
                    </Center>
                ) : sortedProjects.length === 0 ? (
                    <Center py="xl">
                        <Stack align="center">
                            <Text c="dimmed">No orders found</Text>
                            {filtersActive && (
                                <Button
                                    variant="light"
                                    onClick={() => {
                                        setNameFilter('')
                                        setCustomerIdFilter(null)
                                        setScheduleDateRange([null, null])
                                        setStatusFilter([])
                                    }}
                                >
                                    Clear filters
                                </Button>
                            )}
                        </Stack>
                    </Center>
                ) : (
                    <Stack gap="md">
                        {sortedProjects.map((project) => (
                            <ProjectListCard key={project.id} project={project} />
                        ))}
                    </Stack>
                )}
            </Stack>
        </>
    )
}
